import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { deleteCalendarEventFull } from "@/lib/calendarApi";
import { ChevronDown, ChevronLeft, ChevronRight, MapPin, Plus, Repeat, Trash2 } from "lucide-react";
import {
  bucketEventsByDay,
  dateKey,
  layoutOverlaps,
  monthLabel,
  visibleRange,
  type CalendarView,
  type DayBucketEntry,
} from "@/lib/calendarLayout";
import { useCalendarEvents } from "./useCalendarEvents";
import { EditEventModal } from "./EditEventModal";
import { BlockTimeModal } from "./BlockTimeModal";
import { formatTime, showAsDot, type CalEvent } from "./types";

const ACCENT = "#0f6cbd";
const HOUR_HEIGHT = 48; // px per hour in the time grid
const GRID_HEIGHT = HOUR_HEIGHT * 24;
const DEFAULT_SCROLL_HOUR = 6; // scroll the grid to 6 AM on open, matching Outlook's default view

const VIEW_OPTIONS: Array<{ value: CalendarView; label: string }> = [
  { value: "day", label: "Day" },
  { value: "work-week", label: "Work week" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

function hourLabel(h: number): string {
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

/** Eastern-local minutes-since-midnight for "right now" — used only for the current-time line. */
function nowEasternMinutes(): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date());
  const hh = Number(parts.find((p) => p.type === "hour")?.value ?? "0") % 24;
  const mm = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return hh * 60 + mm;
}

/** Today's date key in America/New_York, so "today" is correct even if the browser is in another zone. */
function todayEasternKey(): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function dayHeaderParts(d: Date): { weekday: string; day: number } {
  return {
    weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
    day: d.getDate(),
  };
}

/** Read-only event details popup for events the viewer doesn't organize (isOrganizer !== true). */
function EventDetailsDialog({
  event,
  onClose,
  onRemoved,
}: {
  event: CalEvent;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const canRemove = event.isOrganizer === false;

  const removeMutation = useMutation({
    mutationFn: () => deleteCalendarEventFull(event.id),
    onSuccess: () => {
      toast({ title: "Removed from your calendar" });
      onRemoved();
      onClose();
    },
    onError: (err: Error) => toast({ title: "Failed to remove event", description: err.message, variant: "destructive" }),
  });

  async function handleRemove() {
    const ok = await askConfirm({
      title: "Remove this event from your calendar?",
      name: event.subject ?? undefined,
      subject: "event",
      body: "will be removed from your calendar.",
      lines: ["This declines the invite for you only."],
      confirmLabel: "Remove",
      destructive: true,
    });
    if (ok) removeMutation.mutate();
  }

  const isRecurring = event.type === "occurrence" || event.type === "seriesMaster" || event.type === "exception";
  const timeLabel = event.isAllDay
    ? "All day"
    : `${formatTime(event.start.dateTime)} – ${formatTime(event.end.dateTime)}`;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            {event.subject || "(No title)"}
            {isRecurring && <Repeat className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <p className="flex items-center gap-1.5 text-muted-foreground">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", showAsDot(event.showAs))} />
            {timeLabel}
          </p>
          {event.location?.displayName?.trim() && (
            <p className="flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              {event.location.displayName}
            </p>
          )}
          {event.organizer?.emailAddress?.name && (
            <p className="text-muted-foreground">Organizer: {event.organizer.emailAddress.name}</p>
          )}
          {event.bodyPreview && <p className="whitespace-pre-wrap text-foreground">{event.bodyPreview}</p>}
        </div>
        <DialogFooter>
          {event.webLink && (
            <Button variant="outline" asChild>
              <a href={event.webLink} target="_blank" rel="noopener noreferrer">Open in Outlook</a>
            </Button>
          )}
          {canRemove && (
            <Button variant="destructive" onClick={handleRemove} disabled={removeMutation.isPending}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Remove from my calendar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One event chip in the time grid (day/work-week/week views). Renders a CLIPPED day segment — see DayBucketEntry. */
function TimedChip({ entry, col, totalCols, onClick }: { entry: DayBucketEntry<CalEvent>; col: number; totalCols: number; onClick: () => void }) {
  const { event, segmentStartMin, isContinuation } = entry;
  const endMin = Math.max(entry.segmentEndMin, segmentStartMin + 15);
  const top = (segmentStartMin / 60) * HOUR_HEIGHT;
  const height = Math.max(((endMin - segmentStartMin) / 60) * HOUR_HEIGHT, 16);
  const widthPct = 100 / totalCols;
  const isRecurring = event.type === "occurrence" || event.type === "seriesMaster" || event.type === "exception";
  const tentative = event.showAs === "tentative";

  return (
    <button
      onClick={onClick}
      title={event.subject ?? "(No title)"}
      className={cn(
        "absolute overflow-hidden rounded-[3px] border px-1.5 py-0.5 text-left text-[11px] leading-tight shadow-sm hover:brightness-95",
        tentative ? "border-dashed" : "border-solid"
      )}
      style={{
        top,
        height,
        left: `calc(${col * widthPct}% + 1px)`,
        width: `calc(${widthPct}% - 2px)`,
        backgroundColor: tentative ? "#e8f2fc" : "#cfe4fa",
        borderColor: ACCENT,
        color: "#0b3d66",
        zIndex: 1,
      }}
    >
      <span className="flex items-center gap-1 font-medium">
        {isContinuation && <ChevronRight className="h-2.5 w-2.5 shrink-0 opacity-70" />}
        <span className="truncate">{event.subject || "(No title)"}</span>
        {isRecurring && <Repeat className="h-2.5 w-2.5 shrink-0 opacity-70" />}
      </span>
      {height > 28 && (
        <span className="block truncate opacity-80">
          {isContinuation ? `until ${formatTime(event.end.dateTime)}` : formatTime(event.start.dateTime)}
        </span>
      )}
    </button>
  );
}

/** Small all-day / month-cell chip. `isContinuation` marks a later day of a multi-day event. */
function MiniChip({ entry, onClick }: { entry: DayBucketEntry<CalEvent>; onClick: () => void }) {
  const { event, isContinuation } = entry;
  const isRecurring = event.type === "occurrence" || event.type === "seriesMaster" || event.type === "exception";
  const tentative = event.showAs === "tentative";
  return (
    <button
      onClick={onClick}
      title={event.subject ?? "(No title)"}
      className={cn(
        "flex w-full items-center gap-1 truncate rounded-[3px] border px-1 py-[1px] text-left text-[11px]",
        tentative ? "border-dashed" : "border-solid"
      )}
      style={{ backgroundColor: tentative ? "#e8f2fc" : "#cfe4fa", borderColor: ACCENT, color: "#0b3d66" }}
    >
      {isContinuation && <ChevronRight className="h-2.5 w-2.5 shrink-0 opacity-70" />}
      {isRecurring && <Repeat className="h-2.5 w-2.5 shrink-0 opacity-70" />}
      <span className="truncate">{event.subject || "(No title)"}</span>
    </button>
  );
}

/** Small ‹month picker› in the left rail — pure date navigation, no fetching of its own. */
function MiniMonthPicker({ anchor, onPick }: { anchor: Date; onPick: (d: Date) => void }) {
  const [cursor, setCursor] = useState(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  useEffect(() => setCursor(new Date(anchor.getFullYear(), anchor.getMonth(), 1)), [anchor.getFullYear(), anchor.getMonth()]);

  const grid = useMemo(() => visibleRange("month", cursor).days, [cursor]);
  const todayKey = todayEasternKey();
  const anchorKey = dateKey(anchor);

  return (
    <div className="select-none px-2 pb-3 pt-2 text-[11px]">
      <div className="mb-1 flex items-center justify-between px-1">
        <button
          className="rounded p-0.5 text-neutral-500 hover:bg-neutral-200"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="font-semibold text-neutral-700">{monthLabel(cursor)}</span>
        <button
          className="rounded p-0.5 text-neutral-500 hover:bg-neutral-200"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 text-center text-neutral-400">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={i}>{d}</span>
        ))}
        {grid.map((d) => {
          const key = dateKey(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          return (
            <button
              key={key}
              onClick={() => onPick(d)}
              className={cn(
                "mx-auto flex h-5 w-5 items-center justify-center rounded-full",
                !inMonth && "text-neutral-300",
                key === anchorKey ? "text-white" : key === todayKey ? "font-semibold" : "hover:bg-neutral-200"
              )}
              style={key === anchorKey ? { backgroundColor: ACCENT } : key === todayKey ? { color: ACCENT } : undefined}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function OutlookCalendar() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<CalendarView>("work-week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalEvent | null>(null);
  const [viewingEvent, setViewingEvent] = useState<CalEvent | null>(null);
  const gridScrollRef = useRef<HTMLDivElement>(null);

  const range = useMemo(() => visibleRange(view, anchor), [view, anchor]);
  const { events, configured, isLoading } = useCalendarEvents(range.fetchStart, range.fetchEnd);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["calendar-events"] });

  // Scroll the time grid to a sane default (6 AM) whenever the view/day set changes.
  useEffect(() => {
    if ((view === "day" || view === "work-week" || view === "week") && gridScrollRef.current) {
      gridScrollRef.current.scrollTop = DEFAULT_SCROLL_HOUR * HOUR_HEIGHT;
    }
  }, [view, range.days.length ? dateKey(range.days[0]) : ""]);

  const buckets = useMemo(() => bucketEventsByDay(events, range.days), [events, range.days]);
  const todayKey = todayEasternKey();

  const handleChipClick = (ev: CalEvent) => {
    if (ev.isOrganizer === true) setEditingEvent(ev);
    else setViewingEvent(ev);
  };

  const goToday = () => setAnchor(new Date());
  const step = (dir: 1 | -1) => {
    const days = view === "day" ? 1 : view === "month" ? 0 : view === "work-week" ? 7 : 7;
    if (view === "month") {
      setAnchor((prev) => new Date(prev.getFullYear(), prev.getMonth() + dir, 1));
    } else {
      setAnchor((prev) => {
        const r = new Date(prev);
        r.setDate(r.getDate() + dir * days);
        return r;
      });
    }
  };

  const periodLabel = useMemo(() => {
    if (view === "month") return monthLabel(anchor);
    const first = range.days[0];
    const last = range.days[range.days.length - 1];
    const sameMonth = first.getMonth() === last.getMonth();
    const fmt = (d: Date, withYear: boolean) =>
      d.toLocaleDateString("en-US", withYear ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" });
    if (view === "day") return fmt(first, true);
    return sameMonth ? `${fmt(first, false)} – ${last.getDate()}, ${last.getFullYear()}` : `${fmt(first, true)} – ${fmt(last, true)}`;
  }, [view, anchor, range.days]);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white text-[13px] text-neutral-900">
      {/* Toolbar */}
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-neutral-200 bg-white px-3">
        <Button
          size="sm"
          className="h-7 gap-1"
          style={{ backgroundColor: ACCENT }}
          onClick={() => setNewEventOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          New event
          <ChevronDown className="h-3 w-3 opacity-80" />
        </Button>

        <div className="ml-2 flex items-center gap-0.5 rounded border border-neutral-200 p-0.5">
          {VIEW_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setView(o.value)}
              className={cn(
                "rounded px-2 py-0.5 text-xs",
                view === o.value ? "text-white" : "text-neutral-600 hover:bg-neutral-100"
              )}
              style={view === o.value ? { backgroundColor: ACCENT } : undefined}
            >
              {o.label}
            </button>
          ))}
        </div>

        <div className="ml-2 flex items-center gap-1">
          <button onClick={() => step(-1)} className="rounded p-1 text-neutral-500 hover:bg-neutral-100">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={goToday}>
            Today
          </Button>
          <button onClick={() => step(1)} className="rounded p-1 text-neutral-500 hover:bg-neutral-100">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <span className="ml-1 font-semibold" style={{ color: ACCENT }}>{periodLabel}</span>
      </div>

      {newEventOpen && (
        <BlockTimeModal
          dialogTitle="New event"
          defaultDate={dateKey(anchor)}
          onClose={() => setNewEventOpen(false)}
          onSaved={invalidate}
        />
      )}
      {editingEvent && (
        <EditEventModal event={editingEvent} onClose={() => setEditingEvent(null)} onSaved={invalidate} />
      )}
      {viewingEvent && (
        <EventDetailsDialog event={viewingEvent} onClose={() => setViewingEvent(null)} onRemoved={invalidate} />
      )}

      {!isLoading && !configured ? (
        <div className="flex flex-1 items-center justify-center p-8 text-center text-neutral-400">
          <p className="max-w-sm text-sm">
            Outlook calendar not connected. An admin needs to grant Calendars.Read /
            Calendars.ReadWrite in the Azure portal.
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* Left rail: mini month picker */}
          <div className="w-[220px] shrink-0 overflow-y-auto border-r border-neutral-200 bg-[#f5f5f5]">
            <MiniMonthPicker anchor={anchor} onPick={setAnchor} />
          </div>

          {/* Main grid */}
          {view === "month" ? (
            <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6 overflow-y-auto border-l border-neutral-100">
              {range.days.map((d) => {
                const key = dateKey(d);
                const bucket = buckets.get(key) ?? { allDay: [], timed: [] };
                const all = [...bucket.allDay, ...bucket.timed].sort(
                  (a, b) => a.segmentStartMin - b.segmentStartMin
                );
                const visible = all.slice(0, 3);
                const overflow = all.length - visible.length;
                const inMonth = d.getMonth() === anchor.getMonth();
                return (
                  <div
                    key={key}
                    className={cn(
                      "flex min-h-[96px] flex-col gap-0.5 border-b border-r border-neutral-100 p-1",
                      !inMonth && "bg-neutral-50/60"
                    )}
                  >
                    <span
                      className={cn(
                        "self-end text-[11px]",
                        !inMonth ? "text-neutral-300" : key === todayKey ? "font-semibold" : "text-neutral-600"
                      )}
                      style={key === todayKey ? { color: ACCENT } : undefined}
                    >
                      {d.getDate()}
                    </span>
                    {visible.map((entry) => (
                      <MiniChip key={`${entry.event.id}-${entry.isContinuation}`} entry={entry} onClick={() => handleChipClick(entry.event)} />
                    ))}
                    {overflow > 0 && <span className="text-[10px] text-neutral-500">+{overflow} more</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {/* Day headers */}
              <div className="flex border-b border-l border-neutral-200 pl-12">
                {range.days.map((d) => {
                  const key = dateKey(d);
                  const { weekday, day } = dayHeaderParts(d);
                  return (
                    <div
                      key={key}
                      className="flex flex-1 flex-col items-center border-r border-neutral-100 py-1.5"
                    >
                      <span className="text-[10px] uppercase tracking-wide text-neutral-500">{weekday}</span>
                      <span
                        className={cn(
                          "flex h-6 w-6 items-center justify-center rounded-full text-sm",
                          key === todayKey ? "text-white font-semibold" : "text-neutral-800"
                        )}
                        style={key === todayKey ? { backgroundColor: ACCENT } : undefined}
                      >
                        {day}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* All-day row */}
              {range.days.some((d) => (buckets.get(dateKey(d))?.allDay.length ?? 0) > 0) && (
                <div className="flex border-b border-l border-neutral-200 pl-12">
                  {range.days.map((d) => {
                    const key = dateKey(d);
                    const allDay = buckets.get(key)?.allDay ?? [];
                    return (
                      <div key={key} className="flex min-h-[24px] flex-1 flex-col gap-0.5 border-r border-neutral-100 p-0.5">
                        {allDay.map((entry) => (
                          <MiniChip key={`${entry.event.id}-${entry.isContinuation}`} entry={entry} onClick={() => handleChipClick(entry.event)} />
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Scrollable time grid */}
              <div ref={gridScrollRef} className="min-h-0 flex-1 overflow-y-auto">
                <div className="relative flex border-l border-neutral-200" style={{ height: GRID_HEIGHT }}>
                  {/* Hour rail */}
                  <div className="relative w-12 shrink-0">
                    {Array.from({ length: 24 }, (_, h) => (
                      <div
                        key={h}
                        className="absolute right-1 -translate-y-1/2 text-[10px] text-neutral-400"
                        style={{ top: h * HOUR_HEIGHT }}
                      >
                        {h > 0 && hourLabel(h)}
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  {range.days.map((d, i) => {
                    const key = dateKey(d);
                    const timed = buckets.get(key)?.timed ?? [];
                    const layout = layoutOverlaps(timed);
                    const layoutById = new Map(layout.map((l) => [l.id, l]));
                    const isToday = key === todayKey;
                    return (
                      <div
                        key={key}
                        className={cn("relative flex-1 border-r border-neutral-100", isToday && "bg-blue-50/30")}
                        style={{ minWidth: 0 }}
                      >
                        {/* Hour gridlines */}
                        {Array.from({ length: 24 }, (_, h) => (
                          <div
                            key={h}
                            className="absolute left-0 right-0 border-t border-neutral-100"
                            style={{ top: h * HOUR_HEIGHT }}
                          />
                        ))}
                        {/* Current-time indicator */}
                        {isToday && (
                          <div
                            className="absolute left-0 right-0 z-10 border-t-2"
                            style={{ top: (nowEasternMinutes() / 60) * HOUR_HEIGHT, borderColor: "#d13438" }}
                          >
                            <span className="absolute -left-1 -top-[5px] h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#d13438" }} />
                          </div>
                        )}
                        {timed.map((entry) => {
                          const l = layoutById.get(entry.event.id);
                          return (
                            <TimedChip
                              key={`${entry.event.id}-${entry.isContinuation}`}
                              entry={entry}
                              col={l?.col ?? 0}
                              totalCols={l?.totalCols ?? 1}
                              onClick={() => handleChipClick(entry.event)}
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
